import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Calculator, FilePlus2, RefreshCw, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";

export default function Multimoneda() {
  const [ind, setInd] = useState(null);
  const [pos, setPos] = useState([]);
  const [diff, setDiff] = useState(null);
  const [cm, setCm] = useState(null);
  const [hist, setHist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fecha, setFecha] = useState(todayISO());
  const [anioCm, setAnioCm] = useState(String(new Date().getFullYear()));
  const [openPos, setOpenPos] = useState(false);
  const [form, setForm] = useState({
    tipo: "cxc_me",
    concepto: "",
    moneda: "USD",
    monto_origen: "",
    tc_origen: "",
    fecha_origen: todayISO(),
    naturaleza: "activo",
    cuenta_codigo: "1.1.03",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [i, p, h] = await Promise.all([
        api.get("/multimoneda/indicadores", { params: { fecha } }),
        api.get("/multimoneda/posiciones"),
        api.get("/multimoneda/historial"),
      ]);
      setInd(i.data);
      setPos(p.data);
      setHist(h.data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [fecha]);
  const posPage = useClientPagination(pos, `${pos.length}`);
  const histPage = useClientPagination(hist, `${hist.length}`);

  const previewDiff = async () => {
    try {
      const { data } = await api.post("/multimoneda/diff-cambio/preview", { fecha });
      setDiff(data);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const contabilizarDiff = async () => {
    if (!window.confirm("¿Generar asiento de diferencia de cambio (borrador)?")) return;
    try {
      const { data } = await api.post("/multimoneda/diff-cambio/contabilizar", { fecha });
      toast.success(`Diff. cambio neto ${fmtCLP(data.preview?.neto || 0)} · borrador en Libro diario`);
      setDiff(null);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const previewCm = async () => {
    try {
      const { data } = await api.post("/multimoneda/correccion-monetaria/preview", {
        fecha,
        anio: parseInt(anioCm, 10),
      });
      setCm(data);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const contabilizarCm = async () => {
    if (!window.confirm("¿Generar asiento de corrección monetaria (borrador)?")) return;
    try {
      const { data } = await api.post("/multimoneda/correccion-monetaria/contabilizar", {
        fecha,
        anio: parseInt(anioCm, 10),
      });
      toast.success(`CM neto ${fmtCLP(data.preview?.neto || 0)} · borrador en Libro diario`);
      setCm(null);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const submitPos = async (e) => {
    e.preventDefault();
    try {
      await api.post("/multimoneda/posiciones", {
        ...form,
        monto_origen: parseFloat(form.monto_origen) || 0,
        tc_origen: parseFloat(form.tc_origen) || 0,
      });
      toast.success("Posición registrada");
      setOpenPos(false);
      setForm({
        tipo: "cxc_me", concepto: "", moneda: "USD", monto_origen: "", tc_origen: "",
        fecha_origen: todayISO(), naturaleza: "activo", cuenta_codigo: "1.1.03",
      });
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const borrarPos = async (id) => {
    if (!window.confirm("¿Eliminar esta posición?")) return;
    try {
      await api.delete(`/multimoneda/posiciones/${id}`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const onTipo = (tipo) => {
    const pasivo = ["cxp_me", "prestamo_uf", "prestamo_usd"].includes(tipo);
    setForm({
      ...form,
      tipo,
      naturaleza: pasivo ? "pasivo" : "activo",
      moneda: tipo.includes("uf") ? "UF" : form.moneda === "UF" && !tipo.includes("uf") ? "USD" : form.moneda,
      cuenta_codigo: pasivo ? (tipo.includes("prestamo") ? "2.2.01" : "2.1.01") : "1.1.03",
    });
  };

  return (
    <Page>
      <PageHeader
        title="Multimoneda y corrección monetaria"
        subtitle="Diferencia de cambio (USD/UF) y CM Art. 41 por IPC · asientos en borrador"
        testid="mm-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm flex items-center gap-1.5">
              Fecha
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
                className="border border-border rounded-sm px-2 py-1 text-sm" />
            </label>
            <button type="button" onClick={load}
              className="inline-flex items-center gap-1.5 border border-border hover:bg-muted text-sm px-3 py-1.5 rounded-sm">
              <RefreshCw className="h-4 w-4" /> Actualizar
            </button>
            <Dialog open={openPos} onOpenChange={setOpenPos}>
              <DialogTrigger asChild>
                <button className="inline-flex items-center gap-1.5 border border-border hover:bg-muted text-sm px-3 py-1.5 rounded-sm">
                  <FilePlus2 className="h-4 w-4" /> Posición
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nueva posición ME / UF</DialogTitle>
                  <DialogDescription>CxC/CxP exportación, banco USD o préstamo en UF/USD.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submitPos} className="space-y-3">
                  <label className="block text-sm">Tipo
                    <select value={form.tipo} onChange={(e) => onTipo(e.target.value)}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm">
                      <option value="cxc_me">CxC moneda extranjera</option>
                      <option value="cxp_me">CxP moneda extranjera</option>
                      <option value="banco_me">Banco ME</option>
                      <option value="prestamo_uf">Préstamo UF</option>
                      <option value="prestamo_usd">Préstamo USD</option>
                      <option value="otro">Otro</option>
                    </select>
                  </label>
                  <label className="block text-sm">Concepto
                    <input required value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })}
                      className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm">Moneda
                      <select value={form.moneda} onChange={(e) => setForm({ ...form, moneda: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm">
                        <option value="USD">USD</option>
                        <option value="UF">UF</option>
                        <option value="CLF">CLF</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </label>
                    <label className="block text-sm">Naturaleza
                      <select value={form.naturaleza} onChange={(e) => setForm({ ...form, naturaleza: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm">
                        <option value="activo">Activo</option>
                        <option value="pasivo">Pasivo</option>
                      </select>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm">Monto origen
                      <input type="number" required min="0.01" step="any" value={form.monto_origen}
                        onChange={(e) => setForm({ ...form, monto_origen: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                    </label>
                    <label className="block text-sm">TC origen
                      <input type="number" required min="0.01" step="any" value={form.tc_origen}
                        onChange={(e) => setForm({ ...form, tc_origen: e.target.value })}
                        placeholder={ind?.usd ? `USD ~ ${ind.usd}` : ""}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-sm">Fecha origen
                      <input type="date" value={form.fecha_origen}
                        onChange={(e) => setForm({ ...form, fecha_origen: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                    </label>
                    <label className="block text-sm">Cuenta
                      <input value={form.cuenta_codigo}
                        onChange={(e) => setForm({ ...form, cuenta_codigo: e.target.value })}
                        className="mt-1 w-full border border-border rounded-sm px-2 py-1.5 text-sm" />
                    </label>
                  </div>
                  <DialogFooter>
                    <button type="submit" className="bg-primary text-primary-foreground text-sm px-3 py-1.5 rounded-sm">Guardar</button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {loading ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <IndCard label="USD" value={ind?.usd != null ? fmtCLP(ind.usd) : "—"} sub={ind?.usd_fecha} />
            <IndCard label="UF" value={ind?.uf != null ? fmtCLP(ind.uf) : "—"} sub={ind?.uf_fecha} />
            <IndCard label="EUR" value={ind?.eur != null ? fmtCLP(ind.eur) : "—"} sub={null} />
            <IndCard label="Fuente" value="mindicador.cl" sub={ind?.consultado?.slice(0, 16)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            <div className="bg-card border border-border rounded-md p-5" data-testid="mm-diff">
              <h3 className="font-display text-xl mb-3">Diferencia de cambio</h3>
              <p className="text-[11px] text-muted-foreground mb-3">
                Revalúa posiciones ME/UF al TC de la fecha. Cuentas 4.2.03 / 5.4.03.
              </p>
              <div className="flex gap-2 mb-3">
                <button type="button" onClick={previewDiff}
                  className="inline-flex items-center gap-1.5 border border-border text-sm px-3 py-1.5 rounded-sm">
                  <Calculator className="h-4 w-4" /> Previsualizar
                </button>
                <button type="button" onClick={contabilizarDiff} disabled={!diff?.detalle?.length}
                  className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground disabled:opacity-40 text-sm px-3 py-1.5 rounded-sm">
                  Contabilizar
                </button>
              </div>
              {diff && (
                <>
                  <Fila label="Posiciones" value={diff.posiciones} />
                  <Fila label="Ganancia" value={fmtCLP(diff.ganancia_total)} tono="text-emerald-700" />
                  <Fila label="Pérdida" value={fmtCLP(diff.perdida_total)} tono="text-destructive" />
                  <Fila label="Neto" value={fmtCLP(diff.neto)} fuerte />
                </>
              )}
            </div>

            <div className="bg-card border border-border rounded-md p-5" data-testid="mm-cm">
              <h3 className="font-display text-xl mb-3">Corrección monetaria</h3>
              <p className="text-[11px] text-muted-foreground mb-3">
                IPC sobre AF, existencias y capital. Régimen 14 A (Art. 41). Cuentas 4.2.04 / 5.4.04.
              </p>
              <label className="text-sm flex items-center gap-1.5 mb-3">
                Año tributario
                <input type="number" value={anioCm} onChange={(e) => setAnioCm(e.target.value)}
                  className="border border-border rounded-sm px-2 py-1 text-sm w-24" />
              </label>
              <div className="flex gap-2 mb-3">
                <button type="button" onClick={previewCm}
                  className="inline-flex items-center gap-1.5 border border-border text-sm px-3 py-1.5 rounded-sm">
                  <Calculator className="h-4 w-4" /> Previsualizar
                </button>
                <button type="button" onClick={contabilizarCm} disabled={!cm?.detalle?.length}
                  className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground disabled:opacity-40 text-sm px-3 py-1.5 rounded-sm">
                  Contabilizar
                </button>
              </div>
              {cm && (
                <>
                  {cm.aviso_regimen && (
                    <p className="text-[11px] text-amber-700 mb-2">{cm.aviso_regimen}</p>
                  )}
                  <Fila label="Ítems" value={cm.items} />
                  <Fila label="Factor IPC" value={`${cm.factor_pct} %`} />
                  <Fila label="Ingreso CM" value={fmtCLP(cm.ingreso_total)} tono="text-emerald-700" />
                  <Fila label="Gasto CM" value={fmtCLP(cm.gasto_total)} tono="text-destructive" />
                  <Fila label="Neto" value={fmtCLP(cm.neto)} fuerte />
                </>
              )}
            </div>
          </div>

          <div className="bg-card border border-border rounded-md overflow-x-auto mb-5">
            <div className="px-4 py-2 border-b border-border label-caps">Posiciones</div>
            <table className="acc-table">
              <thead>
                <tr>
                  <th>Concepto</th><th>Moneda</th><th className="num">Monto</th>
                  <th className="num">TC</th><th>Cuenta</th><th></th>
                </tr>
              </thead>
              <tbody>
                {posPage.pageItems.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted-foreground text-sm py-6">Sin posiciones</td></tr>
                ) : posPage.pageItems.map((p) => (
                  <tr key={p.id}>
                    <td>{p.concepto}<div className="text-[10px] text-muted-foreground uppercase">{p.tipo} · {p.naturaleza}</div></td>
                    <td>{p.moneda}</td>
                    <td className="num">{p.monto_origen}</td>
                    <td className="num">{fmtCLP(p.tc_origen)}</td>
                    <td className="text-[11px] font-mono">{p.cuenta_codigo}</td>
                    <td>
                      <button type="button" onClick={() => borrarPos(p.id)} className="text-muted-foreground hover:text-destructive p-1">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <LibroPagination pagination={posPage.pagination} pageSize={posPage.pageSize} onPageSizeChange={posPage.setPageSize} onPageChange={posPage.setPage} testidPrefix="mm-pos" />
          </div>

          <div className="bg-card border border-border rounded-md overflow-x-auto">
            <div className="px-4 py-2 border-b border-border label-caps">Historial</div>
            <table className="acc-table">
              <thead>
                <tr><th>Fecha</th><th>Tipo</th><th className="num">Neto</th><th>Asiento</th></tr>
              </thead>
              <tbody>
                {histPage.pageItems.length === 0 ? (
                  <tr><td colSpan={4} className="text-center text-muted-foreground text-sm py-6">Sin movimientos</td></tr>
                ) : histPage.pageItems.map((h) => (
                  <tr key={h.id}>
                    <td>{h.fecha}</td>
                    <td className="text-[11px] uppercase">{h.tipo}</td>
                    <td className="num font-semibold">{fmtCLP(h.neto ?? h.monto)}</td>
                    <td className="text-[11px] text-muted-foreground font-mono">{h.asiento_id?.slice(0, 8)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <LibroPagination pagination={histPage.pagination} pageSize={histPage.pageSize} onPageSizeChange={histPage.setPageSize} onPageChange={histPage.setPage} testidPrefix="mm-hist" />
          </div>
        </>
      )}
    </Page>
  );
}

function IndCard({ label, value, sub }) {
  return (
    <div className="bg-card border border-border rounded-md px-4 py-3">
      <div className="label-caps text-muted-foreground">{label}</div>
      <div className="font-semibold num text-lg mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Fila({ label, value, fuerte, tono }) {
  return (
    <div className={`flex justify-between gap-3 py-1.5 text-sm ${fuerte ? "font-semibold border-t border-border mt-1 pt-2.5" : "border-b border-border/60"}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={`num ${tono || ""}`}>{value}</span>
    </div>
  );
}
